/**
 * DialerService - API service for the Quick Call Dialer Modal
 * 
 * Handles all API calls for:
 * - Phone numbers (from numbers)
 * - AI agent templates
 * - Contact search
 * - SMS sending
 * - Task management
 */

import { API_BASE_URL } from '../constants/api';

class DialerService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  /**
   * Get auth headers
   */
  getHeaders() {
    return {
      'Content-Type': 'application/json',
    };
  }

  /**
   * Make API request
   */
  async request(endpoint, options = {}) {
    // baseURL already includes the /api prefix (from API_BASE_URL), so strip a
    // leading /api from the endpoint to avoid a doubled /api/api path.
    const path = endpoint.replace(/^\/api(?=\/|$)/, '');
    const url = `${this.baseURL}${path}`;
    const config = {
      credentials: 'include', // Send HttpOnly auth cookie
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers
      }
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Request failed');
      }
      
      return data;
    } catch (error) {
      console.error(`DialerService request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // ============================================
  // PHONE NUMBERS
  // ============================================

  /**
   * Get available phone numbers for outbound calls
   * @returns {Promise<Array>} List of phone numbers
   */
  async getPhoneNumbers() {
    try {
      const response = await this.request('/api/settings/phone-numbers?status=active');
      
      // Handle different response formats
      const numbers = response.phoneNumbers || response.data || response;
      
      if (!Array.isArray(numbers) || numbers.length === 0) {
        // Return default if no numbers configured
        return [{
          id: 'default',
          phoneNumber: '(469) 757-8744',
          isDefault: true,
          friendlyName: 'Default'
        }];
      }
      
      return numbers.map(n => ({
        id: n._id || n.id,
        phoneNumber: this.formatPhoneNumber(n.phoneNumber || n.number),
        isDefault: n.isDefault || n.isGlobalDefault || false,
        friendlyName: n.friendlyName || n.name || null
      }));
    } catch (error) {
      console.error('Error fetching phone numbers:', error);
      // Return fallback
      return [{
        id: 'default',
        phoneNumber: '(469) 757-8744',
        isDefault: true,
        friendlyName: 'Default'
      }];
    }
  }

  // ============================================
  // AI AGENTS
  // ============================================

  /**
   * Get available AI agent templates
   * @returns {Promise<Array>} List of agent templates
   */
  async getAgentTemplates() {
    try {
      const response = await this.request('/api/ai/modular-agents/templates');
      
      // Handle different response formats
      const agents = response.templates || response.agents || response.data || response;
      
      if (!Array.isArray(agents) || agents.length === 0) {
        // Return default agents if none configured
        return [
          { id: 'jane', name: 'Jane - Sales Specialist' },
          { id: 'fred', name: 'Fred - Follow-up Agent' },
          { id: 'support', name: 'Support Agent' }
        ];
      }
      
      return agents.map(a => ({
        id: a._id || a.id,
        name: a.name || a.displayName || 'Unnamed Agent'
      }));
    } catch (error) {
      console.error('Error fetching agent templates:', error);
      // Return fallback agents
      return [
        { id: 'jane', name: 'Jane - Sales Specialist' },
        { id: 'fred', name: 'Fred - Follow-up Agent' }
      ];
    }
  }

  // ============================================
  // CONTACT SEARCH
  // ============================================

  /**
   * Search for a contact by phone number
   * @param {string} phoneNumber - Phone number to search (digits only)
   * @returns {Promise<Object|null>} Contact object or null
   */
  async searchContact(phoneNumber) {
    try {
      // Clean the phone number
      const digits = phoneNumber.replace(/\D/g, '');
      
      if (digits.length < 7) {
        return null;
      }
      
      // Try multiple search endpoints
      let contact = null;

      // Search members by phone (cases/search-members matches on phoneNumber).
      try {
        const response = await this.request(`/api/cases/search-members?search=${digits}`);
        const members = response.members || response.data || [];

        if (members.length > 0) {
          const m = members[0];
          contact = {
            id: m._id || m.patientId,
            name: m.fullName || `${m.firstName || ''} ${m.lastName || ''}`.trim() || 'Unknown',
            phone: m.phoneNumber || m.phone || digits,
            email: m.email,
            location: m.city && m.state ? `${m.city}, ${m.state}` : null,
            status: m.status || 'Active',
            lastContact: m.lastVisit || m.updatedAt,
            role: null
          };
        }
      } catch (e) {
        // Member search failed
      }

      return contact;
    } catch (error) {
      console.error('Error searching contact:', error);
      return null;
    }
  }

  // ============================================
  // SMS
  // ============================================

  /**
   * Send an SMS message
   * @param {Object} params - SMS parameters
   * @param {string} params.to - Recipient phone number
   * @param {string} params.message - Message content
   * @param {string} [params.contactId] - Optional contact ID
   * @returns {Promise<Object>} Send result
   */
  async sendSms({ to, message, contactId, contactName }) {
    try {
      // Route through the inbox so the message is recorded in a conversation
      // thread and shows up in the Inbox / member Messaging tab. Creating with
      // id "new" finds-or-creates the conversation for this phone number.
      const response = await this.request('/api/inbox/conversations/new/messages', {
        method: 'POST',
        body: JSON.stringify({
          text: message,
          phoneNumber: to,
          contactName,
          contactId
        })
      });

      return response;
    } catch (error) {
      console.error('Error sending SMS:', error);
      throw error;
    }
  }

  // ============================================
  // TASKS
  // ============================================

  /**
   * Get pending tasks
   * @param {Object} params - Query parameters
   * @param {string} [params.status] - Task status filter
   * @returns {Promise<Array>} List of tasks
   */
  async getTasks(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const response = await this.request(`/api/tasks${queryString ? `?${queryString}` : ''}`);
      
      // Handle different response formats
      const tasks = response.tasks || response.data || response;
      
      if (!Array.isArray(tasks)) {
        return [];
      }
      
      return tasks.map(t => ({
        id: t._id || t.id,
        title: t.title,
        description: t.description || t.notes,
        dueDate: t.dueDate || t.scheduledFor,
        type: t.type || (t.title?.toLowerCase().includes('callback') ? 'callback' : 'followup'),
        contactId: t.contactId || t.patientId,
        contactName: t.contactName || t.patientName,
        contactPhone: t.contactPhone || t.phoneNumber,
        status: t.status
      }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      // Return empty array on error
      return [];
    }
  }

  /**
   * Create a new task
   * @param {Object} task - Task data
   * @returns {Promise<Object>} Created task
   */
  async createTask(task) {
    try {
      const response = await this.request('/api/tasks', {
        method: 'POST',
        body: JSON.stringify({
          title: task.title,
          description: task.description,
          dueDate: task.dueDate,
          contactId: task.contactId,
          contactPhone: task.contactPhone,
          type: task.type
        })
      });
      
      return response;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  }

  /**
   * Mark a task as complete
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Updated task
   */
  async completeTask(taskId) {
    try {
      const response = await this.request(`/api/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: 'completed',
          completedAt: new Date().toISOString()
        })
      });
      
      return response;
    } catch (error) {
      console.error('Error completing task:', error);
      throw error;
    }
  }

  // ============================================
  // UTILITY
  // ============================================

  /**
   * Format phone number for display
   */
  formatPhoneNumber(phone) {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    
    return phone;
  }
}

// Export singleton instance
const dialerService = new DialerService();
export default dialerService;
